// src/pages/Automations/hooks/useTemplateBasics.js
// Custom hook for managing template basics (global template variables)

import { useState, useEffect, useCallback } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || '';
const STORAGE_KEY = 'proirp_template_basics';

const DEFAULT_BASICS = {
  agent_name: '',
  agent_phone: '',
  agent_email: '',
  agency_name: '',
  office_hours: '',
  appointment_link: ''
};

export function useTemplateBasics() {
  const [basics, setBasics] = useState(DEFAULT_BASICS);
  const [proirpPhone, setProirpPhone] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Load from localStorage
  const loadFromStorage = useCallback(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const data = JSON.parse(stored);
        setBasics({ ...DEFAULT_BASICS, ...data });
        return data;
      }
    } catch (e) {
      console.error('[useTemplateBasics] Failed to load from storage:', e);
    }
    return null;
  }, []);

  // Save to localStorage
  const saveToStorage = useCallback((data) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (e) {
      console.error('[useTemplateBasics] Failed to save to storage:', e);
    }
  }, []);

  // Fetch from API
  const fetchBasics = useCallback(async () => {
    console.log('[useTemplateBasics] Fetching from API...');
    try {
      const response = await fetch(`${API_BASE}/api/automations/template-basics`, {
        credentials: 'include'
      });

      console.log('[useTemplateBasics] Fetch response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('[useTemplateBasics] Fetch response data:', data);
        if (data.ok && data.basics) {
          const newBasics = { ...DEFAULT_BASICS, ...data.basics };
          console.log('[useTemplateBasics] Setting basics from API:', newBasics);
          setBasics(newBasics);
          saveToStorage(newBasics);
          // Store the Pro IRP assigned phone number
          if (data.proirp_phone) {
            setProirpPhone(data.proirp_phone);
          }
          return newBasics;
        } else {
          console.log('[useTemplateBasics] API returned empty basics');
        }
      } else {
        console.log('[useTemplateBasics] Fetch failed with status:', response.status);
      }
    } catch (e) {
      console.error('[useTemplateBasics] Failed to fetch:', e);
    }
    return null;
  }, [saveToStorage]);

  // Save to API
  const saveBasics = useCallback(async (newBasics) => {
    setSaving(true);
    setError(null);

    // Optimistic update
    const prevBasics = { ...basics };
    const mergedBasics = { ...basics, ...newBasics };
    setBasics(mergedBasics);

    console.log('[useTemplateBasics] Attempting to save:', mergedBasics);

    try {
      const response = await fetch(`${API_BASE}/api/automations/template-basics`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(mergedBasics)
      });

      const data = await response.json();
      console.log('[useTemplateBasics] Save response:', data);

      if (!response.ok || !data.ok) {
        throw new Error(data.error || 'Failed to save template basics');
      }

      // Use the returned basics from server (source of truth)
      const savedBasics = data.basics || mergedBasics;
      setBasics(savedBasics);
      saveToStorage(savedBasics);
      console.log('[useTemplateBasics] Saved and stored:', savedBasics);
      return true;
    } catch (e) {
      console.error('[useTemplateBasics] Save failed:', e);
      // Revert optimistic update
      setBasics(prevBasics);
      setError('Failed to save template basics');
      return false;
    } finally {
      setSaving(false);
    }
  }, [basics, saveToStorage]);

  // Replace template variables with actual values
  const replaceVariables = useCallback((template, clientData = {}) => {
    if (!template) return '';

    let result = template;

    // Replace agent/agency variables
    result = result.replace(/\{AgentName\}/g, basics.agent_name || 'Your Agent');
    result = result.replace(/\{AgentPhone\}/g, basics.agent_phone || '');
    result = result.replace(/\{AgentEmail\}/g, basics.agent_email || '');
    result = result.replace(/\{AgencyName\}/g, basics.agency_name || '');
    result = result.replace(/\{OfficeHours\}/g, basics.office_hours || '');
    result = result.replace(/\{AppointmentLink\}/g, basics.appointment_link || '');

    // Replace client variables (with sample data for preview)
    result = result.replace(/\{ClientName\}/g, clientData.name || 'Patricia Smith');
    result = result.replace(/\{ClientFirstName\}/g, clientData.firstName || 'Patricia');
    result = result.replace(/\{CarrierName\}/g, clientData.carrierName || 'Aetna');
    result = result.replace(/\{PlanName\}/g, clientData.planName || 'Aetna Medicare Advantage');
    result = result.replace(/\{EffectiveDate\}/g, clientData.effectiveDate || '01/01/2025');
    result = result.replace(/\{PolicyYear\}/g, clientData.policyYear || new Date().getFullYear().toString());
    result = result.replace(/\{Birthday\}/g, clientData.birthday || 'March 15');
    result = result.replace(/\{DrugName\}/g, clientData.drugName || 'Eliquis');
    result = result.replace(/\{LanguagePreference\}/g, clientData.languagePreference || 'English');

    return result;
  }, [basics]);

  // Get available variables for template editor
  const getAvailableVariables = useCallback(() => {
    return [
      { key: '{ClientName}', label: 'Client Name', preview: 'Patricia Smith' },
      { key: '{ClientFirstName}', label: 'Client First Name', preview: 'Patricia' },
      { key: '{CarrierName}', label: 'Carrier Name', preview: 'Aetna' },
      { key: '{DrugName}', label: 'Drug Name', preview: 'Eliquis' },
      { key: '{AgentName}', label: 'Agent Name', preview: basics.agent_name || 'Your Agent' },
      { key: '{AgentPhone}', label: 'Agent Phone', preview: basics.agent_phone || '(555) 123-4567' },
      { key: '{AgentEmail}', label: 'Agent Email', preview: basics.agent_email || 'agent@example.com' },
      { key: '{AgencyName}', label: 'Agency Name', preview: basics.agency_name || 'Medicare Services' },
      { key: '{PlanName}', label: 'Plan Name', preview: 'Aetna Medicare Advantage' },
      { key: '{EffectiveDate}', label: 'Effective Date', preview: '01/01/2025' },
      { key: '{PolicyYear}', label: 'Policy Year', preview: new Date().getFullYear().toString() },
      { key: '{Birthday}', label: 'Birthday', preview: 'March 15' }
    ];
  }, [basics]);

  // Initial fetch
  useEffect(() => {
    const init = async () => {
      setLoading(true);

      // Load from storage first for instant UI
      const storedData = loadFromStorage();

      // Then fetch from API (API data takes priority)
      const apiData = await fetchBasics();

      // If API returned nothing but we have stored data, use stored data
      if (!apiData && storedData) {
        console.log('[useTemplateBasics] Using localStorage data (API returned empty)');
        setBasics({ ...DEFAULT_BASICS, ...storedData });
      }

      setLoading(false);
    };

    init();
  }, [loadFromStorage, fetchBasics]);

  return {
    basics,
    proirpPhone,
    loading,
    saving,
    error,
    saveBasics,
    replaceVariables,
    getAvailableVariables,
    refetch: fetchBasics
  };
}

export default useTemplateBasics;
